import { memo, useMemo } from 'react';

import { AnimatedNumber } from '@/shadcn/animatedNumber';
import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';

import { BatchCompletionFeed } from '@/components/batchAction/batchCompletionFeed';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import type { BatchJob } from '@/hooks/useBatchJob';
import { isTerminalBatchJobStatus } from '@/hooks/useBatchJob';
import type { BatchItemEvent } from '@/hooks/useRecentBatchItems';
import { formatNumber } from '@/utils/numberFormat';
import {
  formatDuration,
  modeLabel,
  progressSegments
} from './batchJobPresentation';
import type { SubmitterIdentity } from './batchSubmitter';
import { BatchSubmitter } from './batchSubmitter';
import { modeTone } from './modeFilters';

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Preparing...',
  QUEUED: 'Queued...',
  RUNNING: 'Running...',
  DONE: 'Completed',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled'
};

// DONE means the JOB finished, not that the work succeeded — a job whose every
// item failed still reports DONE. The label states the outcome instead.
function outcomeLabel(
  job: Pick<BatchJob, 'status' | 'succeeded' | 'failed'>,
  isTerminal: boolean
): string {
  if (!isTerminal) {
    return STATUS_LABEL[job.status] ?? job.status;
  }
  if (job.failed > 0 && job.succeeded === 0) {
    return 'Failed';
  }
  if (job.failed > 0) {
    return 'Completed with failures';
  }
  return STATUS_LABEL[job.status] ?? job.status;
}

type Props = {
  job: BatchJob | null;
  loading?: boolean;
  feed?: {
    items: BatchItemEvent[];
    latestArrivalIds: string[];
    onLoadOlder: () => void;
  };
  onRetryItem?: (traceId: string) => void;
  retryingItems?: ReadonlySet<string>;
  // Submits a NEW batch over the failed subset — a different mechanism from the
  // per-row retry, which re-runs one item inside this same job.
  onRetryFailed?: (failedTraceIds: string[]) => void;
  retrying?: boolean;
  // First page of items still in flight — the feed shows skeleton rows rather
  // than asserting there are none.
  feedLoading?: boolean;
  // Display identity for job.runBy; falls back to the raw email when absent.
  submitter?: SubmitterIdentity;
  // Group label (the config key) -> the name people recognize.
  groupNames?: Record<string, string>;
  // The icon this action declares in the workflow config.
  actionIcon?: string;
};

type Timing = {
  when: string;
  exact: string;
  duration: string;
  running: boolean;
};

function timingOf(job: BatchJob): Timing | null {
  const anchor = job.createdAt ?? job.startedAt;
  if (!anchor) {
    return null;
  }
  const submitted = new Date(anchor);
  const running = !job.completedAt;
  const ended = job.completedAt ? new Date(job.completedAt) : new Date();
  const started = job.startedAt ? new Date(job.startedAt) : null;
  // Fallback only: the job's own span also counts materialize, admission queueing
  // and the digest, so it overstates how long the traces took.
  const span = formatDuration(ended.getTime() - submitted.getTime());
  // First item to start, last to finish. Items that never ran are excluded server
  // side, so a cancelled or pruned manifest cannot stretch it.
  const itemWindow =
    job.itemRunWindowMs == null ? null : formatDuration(job.itemRunWindowMs);

  return {
    // "12 Aug 2026, 11:47" rather than "8/12/2026 11:47:21 AM": the numeric
    // order is ambiguous across locales and the seconds are noise.
    when: submitted.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    exact: [
      `submitted ${submitted.toLocaleString()}`,
      started ? `started ${started.toLocaleString()}` : null,
      job.completedAt
        ? `finished ${new Date(job.completedAt).toLocaleString()}`
        : null,
      itemWindow ? `${itemWindow} running the traces` : null
    ]
      .filter(Boolean)
      .join(' · '),
    duration: itemWindow ?? span,
    running
  };
}

export const BatchJobDetailPane = memo(
  ({
    job,
    loading = false,
    feed,
    onRetryItem,
    retryingItems,
    onRetryFailed,
    retrying = false,
    feedLoading = false,
    submitter,
    groupNames,
    actionIcon
  }: Props) => {
    const { succeededPercent, failedPercent, percent } = useMemo(() => {
      if (!job) {
        return { succeededPercent: 0, failedPercent: 0, percent: 0 };
      }
      const segments = progressSegments(job);
      return { ...segments, percent: segments.processedPercent };
    }, [job]);

    if (!job) {
      return (
        <div
          className="flex h-full items-center justify-center p-6 text-center text-foreground/50 text-sm"
          data-testid="batch-detail-empty"
        >
          {loading
            ? 'Loading batch action...'
            : 'Select a batch action to see its traces.'}
        </div>
      );
    }

    const isTerminal = isTerminalBatchJobStatus(job.status);
    const hasFailures = job.failed > 0;
    const failedTraceIds = job.failedItems.map(item => item.traceId);

    const timing = timingOf(job);

    return (
      <div
        className="flex h-full w-full flex-col"
        data-testid="batch-job-detail-pane"
      >
        <div className="border-border border-b p-3">
          {/* Mirrors the card: action icon, action bold, group italic. */}
          <div className="flex items-center gap-2">
            {actionIcon ? (
              <span
                aria-hidden
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground/5"
                data-icon={actionIcon}
                data-testid="batch-detail-action-icon"
              >
                <DynamicIcon icon={actionIcon} size={14} />
              </span>
            ) : null}
            <span className="font-semibold text-base first-letter:uppercase">
              {job.actionTitle ?? job.actionKey}
            </span>
            <span className="min-w-0 flex-1 truncate text-foreground/60 text-xs italic">
              {groupNames?.[job.groupLabel] ?? job.groupLabel}
            </span>
          </div>

          <div className="mt-1 flex items-center gap-2 text-foreground/60 text-xs">
            <span
              className={cn(
                'rounded px-1.5 py-px font-medium text-[10px]',
                modeTone(job.mode)
              )}
            >
              {modeLabel(job.mode)}
            </span>
            {job.runBy ? (
              <BatchSubmitter
                className="text-foreground/60"
                identity={submitter ?? { email: job.runBy }}
                withBy
              />
            ) : null}
          </div>

          {timing && (
            <div
              className="mt-1 flex items-center gap-1.5 text-foreground/50 text-xs"
              title={timing.exact}
            >
              <span>{timing.when}</span>
              <span aria-hidden>·</span>
              <span className="tabular-nums">
                {timing.running
                  ? `running ${timing.duration}`
                  : timing.duration}
              </span>
            </div>
          )}

          <div className="mt-2 flex items-baseline gap-2">
            {/* Succeeded and failed as separate proportional shares, so partial
                failure shows a red sliver instead of turning the bar red. */}
            <div className="relative flex h-1.5 w-full overflow-hidden rounded-lg bg-foreground/10">
              {!isTerminal && (
                <div className="absolute inset-0 z-1 animate-pulse bg-accent" />
              )}
              <div
                className="z-2 h-full bg-primary transition-all ease-in"
                data-testid="batch-job-progress-bar"
                style={{ width: `${succeededPercent}%` }}
              />
              <div
                className="z-2 h-full bg-destructive transition-all ease-in"
                data-testid="batch-job-progress-bar-failed"
                style={{ width: `${failedPercent}%` }}
              />
            </div>
            <span className="font-semibold text-xs tabular-nums">{`${percent}%`}</span>
          </div>

          <div className="mt-1 flex justify-between text-foreground/60 text-xs tabular-nums">
            <span data-testid="batch-job-status-label">
              {outcomeLabel(job, isTerminal)}
            </span>
            <span data-testid="batch-job-progress-count">
              <AnimatedNumber value={job.processed} /> / {job.total}
            </span>
          </div>
        </div>

        <dl className="grid grid-cols-3 gap-2 border-border border-b p-3 text-center">
          <div>
            <dt className="text-foreground/50 text-xs uppercase">Total</dt>
            <dd
              className="font-semibold text-sm tabular-nums"
              data-testid="batch-total"
            >
              {formatNumber(job.total)}
            </dd>
          </div>
          <div>
            <dt className="text-foreground/50 text-xs uppercase">Succeeded</dt>
            <dd
              className="font-semibold text-green-600 text-sm tabular-nums"
              data-testid="batch-succeeded"
            >
              {formatNumber(job.succeeded)}
            </dd>
          </div>
          <div>
            <dt className="text-foreground/50 text-xs uppercase">Failed</dt>
            <dd
              className={cn(
                'font-semibold text-sm tabular-nums',
                hasFailures ? 'text-destructive' : 'text-foreground/80'
              )}
              data-testid="batch-failed"
            >
              {formatNumber(job.failed)}
            </dd>
          </div>
        </dl>

        {renderItems({
          job,
          feed,
          isTerminal,
          onRetryItem,
          retryingItems,
          feedLoading
        })}

        {isTerminal && hasFailures && onRetryFailed && (
          // Under the traces, not over them: you decide to re-run after reading
          // what failed, and the button stays put while the feed scrolls.
          <div className="flex shrink-0 items-center justify-end gap-2 border-border border-t p-2">
            {failedTraceIds.length < job.failed && (
              <p
                className="min-w-0 flex-1 text-foreground/50 text-xs"
                data-testid="batch-failed-capped-note"
              >
                {`Showing ${failedTraceIds.length} of ${job.failed} failures (list is capped). Retrying re-runs the shown items.`}
              </p>
            )}
            <Button
              className="h-6 shrink-0 cursor-pointer px-2 text-xs"
              data-cy="batch-retry-failed-button"
              data-testid="batch-retry-failed-button"
              disabled={retrying || failedTraceIds.length === 0}
              onClick={() => onRetryFailed(failedTraceIds)}
              size="sm"
            >
              {retrying
                ? 'Retrying...'
                : `Retry ${failedTraceIds.length} failed`}
            </Button>
          </div>
        )}
      </div>
    );
  }
);

function renderItems({
  job,
  feed,
  isTerminal,
  onRetryItem,
  retryingItems,
  feedLoading
}: {
  job: BatchJob;
  feed: Props['feed'];
  isTerminal: boolean;
  onRetryItem: Props['onRetryItem'];
  retryingItems: Props['retryingItems'];
  feedLoading: boolean;
}) {
  if (job.itemsExpired) {
    return (
      <div
        className="flex flex-1 items-center justify-center p-6 text-center text-foreground/50 text-xs"
        data-testid="batch-items-expired"
      >
        Per-trace detail is kept for 90 days.
      </div>
    );
  }
  if (!feed) {
    return null;
  }
  return (
    <div className="min-h-0 flex-1 p-2">
      {/* Keyed by job: two batches over the same traces produce rows with the
          same trace-id keys, so React would REUSE the row elements across a job
          switch and the entrance animation (a mount-time `initial`) would never
          run for the second batch you open. */}
      <BatchCompletionFeed
        items={feed.items}
        key={job.id}
        latestArrivalIds={feed.latestArrivalIds}
        loading={feedLoading}
        onLoadOlder={feed.onLoadOlder}
        onRetryItem={isTerminal ? onRetryItem : undefined}
        retryingItems={retryingItems}
      />
    </div>
  );
}
